import { deleteField, doc, getDoc } from "firebase/firestore";
import { UserData } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { updateUser } from "../models/UserModel";
import { defaultGamificationProps } from "./gamification-helper-methods/progression-and-river-animation";
import { updateLeaderboardForUser } from "../models/LeaderboardModel";
import { UserSubscriptionPlan, SubscriptionStatus } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { db } from "../../backend/utils/init-firebase";

// migration for new illustrations & game design system - may 2025
export async function migrateUserGamificationPropertiesIfNeeded(user: UserData) {
  // if user has no gamification_properties, we need to create new fields and migrate old xp earned to a new field
  if (!user?.gamification_properties || !user?.gamification_properties?.current_level) {
    const all_time_xp_earned = user?.all_time_xp_earned;
    const newGamificationProperties = defaultGamificationProps(all_time_xp_earned ?? undefined);
    await updateUser(user.id, {
      gamification_properties: newGamificationProperties,
      all_time_xp_earned: deleteField(),
      animal_avatar_selected: deleteField(),
      animal_team_selected: deleteField(),
    });

    await updateLeaderboardForUser(user.id, {
      current_week_xp_earned: 0,
      current_month_xp_earned: 0,
      weekly_xp_earned_history: {},
      monthly_xp_earned_history: {},
      all_time_xp_earned: 0,
      animal_avatar_selected: deleteField(),
      animal_team_selected: deleteField(),
    });

    return newGamificationProperties;
  }

  return null;
}

export async function migrateUserBadgesProgressAndStatsIfNeeded(user: UserData) {
  if (!user?.gamification_properties?.badges_progress_and_stats) {
    user.gamification_properties.badges_progress_and_stats = {
      questions_answered_badges: {
        number_of_questions_answered: 0,
        badges: [],
      },
      free_response_questions_answered_badges: {
        number_of_free_response_questions_answered: 0,
        badges: [],
      },
      exams_survived_badges: {
        number_of_exams_survived: 0,
        badges: [],
      },
      daily_reviews_badges: {
        number_of_daily_reviews_completed: 0,
        badges: [],
      },
      mistakes_corrected_badges: {
        number_of_mistakes_corrected: 0,
        badges: [],
      },
      ids_of_earned_badges_user_has_not_seen: [],
    };
    await updateUser(user.id, {
      gamification_properties: user.gamification_properties,
    });
    return user?.gamification_properties?.badges_progress_and_stats;
  }

  return null;
}

// Migration for consolidating MegaMind to Super Learner - Dec 2024
export async function migrateMegaMindUsersToSuperLearner(user: UserData) {
  if (user?.subscription_information?.subscription_plan_type === UserSubscriptionPlan.MEGA_MIND) {
    await updateUser(user.id, {
      subscription_information: {
        ...user.subscription_information,
        subscription_plan_type: UserSubscriptionPlan.SUPER_LEARNER,
        did_get_grandfathered_from_mega_mind_to_super_learner: true,
      },
    });
    return true;
  }
  return false;
}

// Get list of content creator emails and grant Super Learner access if user is in the list
export async function getListOfContentCreatorEmails(userEmail: string, userId: string, currentUser: UserData) {
  try {
    const creatorsDocRef = doc(db, "creators_list", "creators");
    const creatorsDoc = await getDoc(creatorsDocRef);

    if (!creatorsDoc.exists()) {
      return { isCreator: false, emails: [] };
    }

    const creatorsData = creatorsDoc.data();
    const creatorsArray = creatorsData?.creators || [];

    const creatorEmails = creatorsArray.map((creator: any) => creator.email).filter((email: string) => email);

    const isUserInCreatorsList = creatorEmails.includes(userEmail);

    if (isUserInCreatorsList) {
      const hasActiveSubscription = currentUser?.subscription_information?.subscription_status === "active";
      // Elite counts too — never DOWNGRADE an Elite creator to Super Learner.
      const hasSuperLearner =
        currentUser?.subscription_information?.subscription_plan_type === UserSubscriptionPlan.SUPER_LEARNER ||
        currentUser?.subscription_information?.subscription_plan_type === UserSubscriptionPlan.ELITE;

      if (!hasActiveSubscription || !hasSuperLearner) {
        await updateUser(userId, {
          subscription_information: {
            ...currentUser.subscription_information,
            subscription_plan_type: UserSubscriptionPlan.SUPER_LEARNER,
            subscription_status: SubscriptionStatus.ACTIVE,
          },
        });
      }
    }

    return { isCreator: isUserInCreatorsList, emails: creatorEmails };
  } catch (error) {
    console.error("Error fetching content creator emails:", error);
    return { isCreator: false, emails: [] };
  }
}
